/**
 * A small in-memory ring buffer of things that went wrong.
 *
 * Every HTTP call already funnels through one client, so recording failures
 * there costs nothing per call and means a phone with no dev tools can still
 * show the status, the error code, the request id and the body that was sent.
 * Nothing is persisted — it is gone on reload, by design.
 */

const MAX_ENTRIES = 40;

export interface LogEntry {
  id: number;
  at: number;
  kind: "request" | "crash" | "response";
  method?: string;
  path?: string;
  status?: number;
  code?: string;
  message: string;
  requestId?: string | null;
  /** Response `details`, e.g. the Zod tree from validation_failed. */
  details?: unknown;
  /** What we sent, with anything sensitive redacted. */
  sentBody?: unknown;
  /** Only present while response recording is switched on. */
  responseBody?: unknown;
  durationMs?: number;
}

let nextId = 1;
const entries: LogEntry[] = [];
const listeners = new Set<() => void>();

/* ---------------------------------------------------------------------------
   Response recording (opt-in).

   Several of this API's response shapes aren't in the reference document, and
   guessing field names one round trip at a time is slow and error-prone. With
   this on, successful responses are recorded too, so an unexpected payload can
   be read straight off the phone. Off by default; it survives a reload because
   seeing a payload usually means refetching it.
--------------------------------------------------------------------------- */

const RECORD_KEY = "ro.debugResponses";

let recording = (() => {
  try {
    return localStorage.getItem(RECORD_KEY) === "1";
  } catch {
    return false;
  }
})();

export function isRecordingResponses() {
  return recording;
}

export function setRecordingResponses(next: boolean) {
  recording = next;
  try {
    if (next) localStorage.setItem(RECORD_KEY, "1");
    else localStorage.removeItem(RECORD_KEY);
  } catch {
    /* ignore */
  }
  for (const listener of listeners) listener();
}

/**
 * Never let a credential into the log. Auth request bodies are dropped whole;
 * elsewhere only the individual sensitive keys are replaced, because the rest of
 * the body is the thing worth seeing.
 */
const SENSITIVE_KEYS = new Set([
  "password",
  "currentpassword",
  "newpassword",
  "confirm",
  "refreshtoken",
  "accesstoken",
  "turnstiletoken",
  "token",
  "code",
  "inivtecode",
  "invitecode",
  "response",
  "challengeid",
]);

export function redactBody(path: string, body: unknown): unknown {
  if (body === undefined || body === null) return undefined;
  if (typeof FormData !== "undefined" && body instanceof FormData) return "«multipart form data»";
  if (typeof Blob !== "undefined" && body instanceof Blob) return "«binary»";
  // Auth bodies are credentials almost end to end — don't keep any of it.
  if (path.startsWith("/v1/auth")) return "«redacted (auth request)»";

  const walk = (value: unknown, depth = 0): unknown => {
    if (depth > 4) return "…";
    if (Array.isArray(value)) return value.slice(0, 20).map((v) => walk(v, depth + 1));
    if (value && typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
        out[key] = SENSITIVE_KEYS.has(key.toLowerCase()) ? "«redacted»" : walk(val, depth + 1);
      }
      return out;
    }
    if (typeof value === "string" && value.length > 300) return `${value.slice(0, 300)}…`;
    return value;
  };

  return walk(body);
}

/**
 * Records a successful response while recording is on. Auth responses are never
 * recorded — they carry tokens. Long arrays are trimmed so the result stays
 * readable on a phone.
 */
export function recordResponse(init: {
  method: string;
  path: string;
  status: number;
  body: unknown;
  durationMs?: number;
}) {
  if (!recording) return;
  if (init.path.startsWith("/v1/auth")) return;

  recordLogEntry({
    kind: "response",
    method: init.method,
    path: init.path,
    status: init.status,
    message: "Response recorded",
    responseBody: trimForDisplay(init.body),
    durationMs: init.durationMs,
  });
}

/** Keeps the first few items of long arrays, and notes what was dropped. */
function trimForDisplay(value: unknown, depth = 0): unknown {
  if (depth > 5) return "…";
  if (Array.isArray(value)) {
    const head = value.slice(0, 3).map((item) => trimForDisplay(item, depth + 1));
    return value.length > 3 ? [...head, `…${value.length - 3} more`] : head;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key] = trimForDisplay(val, depth + 1);
    }
    return out;
  }
  if (typeof value === "string" && value.length > 200) return `${value.slice(0, 200)}…`;
  return value;
}

export function recordLogEntry(entry: Omit<LogEntry, "id" | "at">) {
  entries.unshift({ ...entry, id: nextId++, at: Date.now() });
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES;
  for (const listener of listeners) listener();
}

export function getLogEntries(): LogEntry[] {
  return entries;
}

export function clearLogEntries() {
  entries.length = 0;
  for (const listener of listeners) listener();
}

export function subscribeToLog(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** One pasteable block, for sending to whoever is going to fix it. */
export function formatLogForSharing(list: LogEntry[] = entries): string {
  if (!list.length) return "No errors recorded.";
  const lines = [
    `Rocketeer Orbit diagnostics — ${new Date().toISOString()}`,
    `${window.location.href}`,
    `${navigator.userAgent}`,
    "",
  ];
  for (const entry of list) {
    lines.push(
      `— ${new Date(entry.at).toISOString()} · ${
        entry.kind === "crash" ? "CRASH" : `${entry.method ?? "?"} ${entry.path ?? "?"}`
      }`,
    );
    if (entry.status !== undefined) lines.push(`  status: ${entry.status}`);
    if (entry.code) lines.push(`  code: ${entry.code}`);
    lines.push(`  message: ${entry.message}`);
    if (entry.requestId) lines.push(`  x-request-id: ${entry.requestId}`);
    if (entry.sentBody !== undefined) lines.push(`  sent: ${safeJson(entry.sentBody)}`);
    if (entry.details !== undefined && entry.details !== null) {
      lines.push(`  details: ${safeJson(entry.details)}`);
    }
    if (entry.responseBody !== undefined) {
      lines.push(`  response: ${safeJson(entry.responseBody)}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

export function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
